import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthState, AuthContextType, LoginCredentials, User, Session, Permission, UserRole } from '../types/auth';
import authService from '../services/authService';

// Ações do reducer
type AuthAction = 
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; session: Session } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'SESSION_REFRESH'; payload: Session }
  | { type: 'CLEAR_ERROR' };

// Estado inicial
const initialState: AuthState = {
  user: null,
  session: null,
  isLoading: false,
  error: null
};

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null
      };
    
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        session: action.payload.session,
        isLoading: false,
        error: null
      };
    
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        session: null,
        isLoading: false,
        error: action.payload
      };
    
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        session: null,
        isLoading: false,
        error: null
      };
    
    case 'SESSION_REFRESH':
      return {
        ...state,
        session: action.payload
      };
    
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    
    default:
      return state;
  }
}

// Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider Props
interface AuthProviderProps {
  children: ReactNode;
}

// Provider
export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Verificar sessão existente ao inicializar
  useEffect(() => {
    const storedToken = localStorage.getItem('prime_scada_token');
    if (storedToken) {
      const validation = authService.validateSession(storedToken);
      if (validation.valid && validation.user && validation.session) {
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user: validation.user, session: validation.session }
        });
      } else {
        localStorage.removeItem('prime_scada_token');
      }
    }
  }, []);

  // Auto-refresh da sessão
  useEffect(() => {
    if (state.session) {
      const timeToExpiry = state.session.expiresAt.getTime() - Date.now();
      const refreshTime = Math.max(timeToExpiry - 15 * 60 * 1000, 60 * 1000); // 15 min antes ou 1 min

      const refreshTimer = setTimeout(() => {
        refreshSession();
      }, refreshTime);

      return () => clearTimeout(refreshTimer);
    }
  }, [state.session]);

  // Implementação das funções
  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const result = await authService.login(credentials);
      
      if (result.success && result.user && result.session) {
        localStorage.setItem('prime_scada_token', result.session.token);
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user: result.user, session: result.session }
        });
        return true;
      } else {
        dispatch({
          type: 'LOGIN_FAILURE',
          payload: result.error || 'Erro no login'
        });
        return false;
      }
    } catch (error) {
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: 'Erro interno do sistema'
      });
      return false;
    }
  };

  const logout = (): void => {
    if (state.session) {
      authService.logout(state.session.id);
    }
    localStorage.removeItem('prime_scada_token');
    dispatch({ type: 'LOGOUT' });
  };

  const refreshSession = async (): Promise<boolean> => {
    if (!state.session) return false;

    const result = authService.refreshSession(state.session.token);
    if (result.success && result.session) {
      localStorage.setItem('prime_scada_token', result.session.token);
      dispatch({ type: 'SESSION_REFRESH', payload: result.session });
      return true;
    } else {
      logout();
      return false;
    }
  };

  const hasPermission = (permission: Permission): boolean => {
    if (!state.user) return false;
    return authService.hasPermission(state.user, permission);
  };

  const hasRole = (role: UserRole): boolean => {
    if (!state.user) return false;
    return authService.hasRole(state.user, role);
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    refreshSession,
    hasPermission,
    hasRole
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook customizado
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}